import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MagicLinkModel } from '@shared/models/platform-tool-models';
import { InvalidateSubject } from '@shared/helpers/invalidate.subject';
import { SecurityState } from '@shared/store/reducers/security.reducer';
import { getUserProfile, isInDemo } from '@shared/store/selectors/security.selector';
import { UserProfile } from '@shared/models/user-info.model';
import {
  browserZone,
  dismissMismatch,
  isIanaZone,
  profileZone,
  shownMismatch,
  shownNoZone,
  zoneDisplayName
} from '@shared/helpers/time-zone-mismatch';

/**
 * ITP-E-335-02. The persistent banner on Home when the profile time zone and this browser's zone differ. Every Home
 * link names it as its BannerComponent (HomePageCommandConsumer, XatBotToolLinkCommandConsumer), and the shared
 * platform-tool-banner mounts it; the server can't know the browser's zone, so this component decides whether to
 * show. Dismiss hides it in this browser until either zone changes.
 * ITP-E-335-04: a profile with no zone gets the banner too, asking for one and naming this browser's zone, except inside
 * a demo, where the profile can't be saved; that Dismiss lasts until this browser's zone changes. Both name zones as
 * people read them ('Dhaka · GMT+6'). It never changes the zone: Update profile and Set time zone open the profile
 * page, the only place a zone is saved.
 */
@Component({
  standalone: false,
  selector: 'profile-time-zone-banner',
  templateUrl: './profile-time-zone-banner.component.html',
  styleUrls: ['./profile-time-zone-banner.component.scss']
})
export class ProfileTimeZoneBannerComponent implements OnInit, OnDestroy {
  @Input() magicLinkModel: MagicLinkModel;
  @Input() handler: any;
  @Input() invalidateSubject: InvalidateSubject;

  /** The two zones, and as people read them, while the mismatch shows; else null. */
  mismatch: { profile: string, browser: string, profileName: string, browserName: string } | null = null;
  /**
   * This browser's zone, and as people read it, while the banner asks the profile for a zone; else null. The name is ''
   * when the browser can't name its zone, and the banner leaves it out.
   */
  noZone: { browser: string, browserName: string } | null = null;

  private readonly browser = this.readBrowserZone();
  private readonly destroy$ = new Subject<void>();

  constructor(private store: Store<SecurityState>, private router: Router) {}

  ngOnInit(): void {
    // The profile store follows Apply on the profile page, so a saved zone hides or moves the banner.
    combineLatest([this.store.pipe(select(getUserProfile)), this.store.pipe(select(isInDemo))])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([userProfile, inDemo]: [UserProfile, boolean]) => {
        const profile = profileZone(userProfile);
        const mismatch = shownMismatch('home', profile, this.browser);
        this.mismatch = mismatch &&
          { ...mismatch, profileName: zoneDisplayName(mismatch.profile), browserName: zoneDisplayName(mismatch.browser) };
        // A demo can't save the profile, and a magic-link participant has none: no ask there.
        const noZone = inDemo ? null : shownNoZone('home', profile, this.browser);
        this.noZone = noZone && { ...noZone, browserName: isIanaZone(noZone.browser) ? zoneDisplayName(noZone.browser) : '' };
      });
  }

  updateProfile(): void {
    this.router.navigateByUrl('/user-profile-settings');
  }

  dismiss(): void {
    if (this.mismatch) {
      dismissMismatch('home', this.mismatch.profile, this.mismatch.browser);
      this.mismatch = null;
    }
    if (this.noZone) {
      dismissMismatch('home', '', this.noZone.browser);
      this.noZone = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** This browser's zone. A method, so a spec can set it: `browserZone`, a module export, can't be spied on. */
  protected readBrowserZone(): string {
    return browserZone();
  }
}
